import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { importItemsActions } from '@common-reducers/ImportItemsReducer';
import { getRuntimeUrl } from '@common-services/storage';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useState } from 'react';
import FileSaver from 'file-saver';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';


type Props = {
    goNext(isFromSheets?: boolean): void;
}

export default function CreateGoogleSheetsAudience({
    goNext,
}: Props) {
    const dispatch = useAppDispatch()
    const { t } = useTranslation(namespace);

    const openedby = useAppSelector((state) => state.ModalReducer.openedby)

    const [link, setLink] = useState('');
    const [sheets, setSheets] = useState([]);
    const [data, setData] = useState([]);
    const [name, setName] = useState('');
    const [isSheetLoading, setIsSheetLoading] = useState(false)
    const [isUnableToLoadSheet, setIsUnableToLoadSheet] = useState(false)

    const API_KEY = 'AIzaSyCCO4W0WCNM3srGW1c6guCGuj7jzBemi3I';


    function trimEmptyRowsAndColumns(spreadsheet: string[][]): string[][] {
        if (!spreadsheet.length) return [];

        const trimmedSheet = spreadsheet.filter((row) => row?.length && row?.length !== 0)
        let count = 0;

        for (let cell of trimmedSheet?.[0]) {
            if (cell === '' || cell === null) {
                count++;
            } else {
                break;
            }
        }

        return trimmedSheet.map((row) => row.slice(count))
    }

    const fetchSheetNames = async (sheetId: string) => {
        try {
            setIsSheetLoading(true)
            const response = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${sheetId}?key=${API_KEY}`);
            const data = await response.json();
            setSheets(data.sheets.map((sheet: { properties: { title: string } }) => sheet.properties.title));
            setName(data?.properties?.title)
            if (data.sheets.length === 1) {
                const match = link.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
                if (match) {
                    fetchSheetData(match[1], data.sheets[0].properties.title)
                }
            }
            setIsSheetLoading(false)
        } catch (error) {
            console.error('Error fetching sheet names:', error);
            setIsSheetLoading(false)
            setIsUnableToLoadSheet(true)
        }
    };


    const fetchSheetData = async (sheetId: string, sheetName: string) => {
        try {
            setIsSheetLoading(true)
            const response = await fetch(`https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${sheetName}?key=${API_KEY}`);
            const data = await response.json();
            setData(data.values || []);

            const _data = trimEmptyRowsAndColumns(data.values)
            const __data = _data.slice(1).map((row) => {
                let rowData = {}
                for (let i = 0; i < row.length; i++) {
                    rowData = {
                        ...rowData,
                        [_data[0][i]]: row[i],
                    }
                }
                return rowData
            })
            await dispatch(importItemsActions.setGoogleSheetsImportProps({
                importFileName: name === '' ? sheetName : name,
                workSheetName: sheetName,
                importJsonData: __data,
            }))
            setIsSheetLoading(false)
            goNext(true)
        } catch (error) {
            console.error('Error fetching sheet data:', error);
            setIsSheetLoading(false)
            setIsUnableToLoadSheet(true)
        }
    };

    const handleSubmit = () => {
        const matches = link.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
        if (matches && matches[1]) {
            fetchSheetNames(matches[1]);
        }
    };


    const downloadFile = () => {
        FileSaver.saveAs(getRuntimeUrl('img/sample.csv'), 'sample.csv');
    };


    return (
        <div className="flex flex-col justify-start items-center w-full">
            {!isSheetLoading && isUnableToLoadSheet && (
                <>
                    <span className={cn(
                        "mt-[70px] font-bold text-[22px] mb-6 block text-center",
                        "text-[var(--color-text)] dark:text-[var(--color-text)]"
                    )}>
                        {t('googleSheets.unableToFetch')}<br /> {t('googleSheets.pleaseCheck')}
                    </span>
                    <ul className="list-disc">
                        <li className="mb-6 text-[var(--color-text)] dark:text-[var(--color-text)]">
                            {t('googleSheets.shareSettingsPublic')}
                        </li>
                    </ul>
                    <Button
                        className="mt-5 p-2 w-[250px] bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90 text-white dark:text-white"
                        onClick={() => setIsUnableToLoadSheet(false)}
                    >
                        {t('googleSheets.iUnderstand')}
                    </Button>
                </>
            )}
            {!isSheetLoading && !isUnableToLoadSheet && sheets.length === 0 && (
                <div className="flex flex-col justify-center items-center w-full mt-2.5">
                    <Input
                        className={cn(
                            "text-[var(--color-primary)] dark:text-[var(--color-primary)]",
                            "w-full max-w-[450px] text-center",
                            "bg-[rgb(240,242,245)] dark:bg-[rgb(240,242,245)]"
                        )}
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        placeholder={t('googleSheets.enterShareLink')}
                    />
                    <Button
                        className="mt-5 p-2 w-[250px] bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90 text-white dark:text-white"
                        onClick={handleSubmit}
                    >
                        {t('googleSheets.fetchSheets')}
                    </Button>
                </div>
            )}
            {!isSheetLoading && !isUnableToLoadSheet && sheets.length > 1 && data.length === 0 && (
                <div className="flex flex-col justify-center items-center w-full h-full">
                    <span className="mt-[345px] text-[var(--color-text)] dark:text-[var(--color-text)]">
                        {t('googleSheets.selectSheet')}
                    </span>
                    <div className="overflow-x-hidden overflow-y-scroll h-[30vh] mt-2.5 scrollbar-thin scrollbar-thumb-[#c5e9f1] scrollbar-track-transparent scrollbar-thumb-rounded">
                        {sheets.map(sheetName => {
                            return (
                                <Button
                                    key={sheetName}
                                    className={cn(
                                        "mt-5 mx-[15px] p-2 w-[250px]",
                                        "flex items-center justify-center",
                                        "bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90",
                                        "text-white dark:text-white"
                                    )}
                                    onClick={() => {
                                        const match = link.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
                                        if (match) {
                                            fetchSheetData(match[1], sheetName)
                                        }
                                    }}
                                >
                                    <span className={cn(
                                        "max-w-full overflow-hidden text-ellipsis whitespace-nowrap inline-block"
                                    )}>
                                        {sheetName}
                                    </span>
                                </Button>
                            );
                        })}
                    </div>
                </div>
            )}
            {isSheetLoading && (
                <div className="relative top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
                </div>
            )}
        </div>
    )
}